import { useEffect, useState } from "react";
import {
  Bot,
  Braces,
  CheckCircle2,
  Clipboard,
  FileOutput,
  ShieldCheck,
  X,
} from "lucide-react";

import { AI_CONTROL_EXAMPLE } from "../domain/ai-control";

type AiControlDialogProps = {
  open: boolean;
  canExport: boolean;
  snapshot: unknown;
  onApply: (plan: unknown) => Promise<{ applied: number; messages: string[] }>;
  onClose: () => void;
  onExport: () => void;
};

export function AiControlDialog({
  open,
  canExport,
  snapshot,
  onApply,
  onClose,
  onExport,
}: AiControlDialogProps) {
  const [planText, setPlanText] = useState(() =>
    JSON.stringify(AI_CONTROL_EXAMPLE, null, 2),
  );
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose, open]);

  if (!open) return null;

  const applyPlan = async () => {
    setApplying(true);
    setStatus("");
    setError("");
    try {
      const result = await onApply(JSON.parse(planText));
      setStatus(`已执行 ${result.applied} 个动作：${result.messages.join("；")}`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "AI 操作计划无法执行");
    } finally {
      setApplying(false);
    }
  };

  const copySnapshot = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(snapshot, null, 2));
      setError("");
      setStatus("已复制当前稿件与模板状态，可交给 AI 生成操作计划。");
    } catch {
      setStatus("");
      setError("复制失败，请检查浏览器剪贴板权限。");
    }
  };

  return (
    <div className="ai-dialog-backdrop" onMouseDown={onClose} role="presentation">
      <section
        aria-labelledby="ai-control-title"
        aria-modal="true"
        className="ai-control-dialog"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
      >
        <header>
          <div className="ai-dialog-title">
            <span className="ai-dialog-icon">
              <Bot size={21} />
            </span>
            <span>
              <small>ADVANCED DEBUG INTERFACE</small>
              <h2 id="ai-control-title">AI 调试 / 高级接口</h2>
            </span>
          </div>
          <span className="ai-ready-badge">
            <span /> READY
          </span>
          <button aria-label="关闭 AI 调试接口" onClick={onClose} type="button">
            <X size={19} />
          </button>
        </header>

        <div className="ai-control-body">
          <div className="ai-capability-grid">
            <article>
              <Braces size={17} />
              <strong>结构化动作</strong>
              <span>AI 只能调用白名单命令</span>
            </article>
            <article>
              <ShieldCheck size={17} />
              <strong>本地校验</strong>
              <span>非法路径与层级会被拒绝</span>
            </article>
            <article>
              <FileOutput size={17} />
              <strong>受控导出</strong>
              <span>Word 仍经过完整审计</span>
            </article>
          </div>

          <div className="ai-control-note">
            <ShieldCheck size={16} />
            <p>
              普通用户无需手写 JSON；日常可直接在 Codex 中调用 <code>$paiban-sci-software</code>
              ，用自然语言让 AI 操作与导出。下方仅供开发调试或其他代理接入。作者身份与邮箱仍保留人工核对，AI
              不得虚构作者事实。
            </p>
          </div>

          <div className="ai-plan-toolbar">
            <span>
              <strong>调试用动作计划</strong>
              <small>JSON · version 1</small>
            </span>
            <button onClick={() => void copySnapshot()} type="button">
              <Clipboard size={14} /> 复制当前状态
            </button>
          </div>
          <label className="ai-plan-field">
            <span className="sr-only">AI 操作计划 JSON</span>
            <textarea
              aria-label="AI 操作计划 JSON"
              autoComplete="off"
              name="ai-control-plan"
              onChange={(event) => setPlanText(event.target.value)}
              spellCheck={false}
              value={planText}
            />
          </label>

          <div aria-live="polite" className="ai-plan-status">
            {error ? <p className="error">{error}</p> : null}
            {status ? (
              <p className="success">
                <CheckCircle2 size={14} /> {status}
              </p>
            ) : null}
          </div>
        </div>

        <footer>
          <span>
            浏览器接口：<code>window.sciStudioAI</code>
          </span>
          <div>
            <button className="ai-secondary-action" onClick={onExport} disabled={!canExport} type="button">
              <FileOutput size={14} /> 请求导出 Word
            </button>
            <button
              className="ai-primary-action"
              disabled={applying || !planText.trim()}
              onClick={() => void applyPlan()}
              type="button"
            >
              <Bot size={14} /> {applying ? "正在执行…" : "校验并执行"}
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}
